import React, { useState } from 'react';
import type { SwiperOptions, Swiper as SwiperType } from 'swiper';
import SwiperCore, {
  Navigation,
  Scrollbar,
  Mousewheel,
  Pagination,
  Thumbs,
  Keyboard,
  FreeMode,
} from 'swiper';
import 'swiper/swiper-bundle.css';
import type {
  HotelPageGallerySwiperProps,
  ImageProps,
  PopupSwiperProps,
} from './types';
import { navigation } from './styles';

import GallerySwiper from './GallerySwiper';
import {
  pushCloseGalleryAnalytics,
  pushOpenGalleryAnalytics,
  pushShowImageAnalytics,
  pushStatsBrokenImage,
} from './analytics';
import { PopupSwiper } from './components/PopupSwiper';
import { ThumbSwiper } from './components/ThumbSwiper';
import { ArrowComponent } from './components/ArrowComponent';

SwiperCore.use([
  Navigation,
  Scrollbar,
  Mousewheel,
  Pagination,
  Thumbs,
  Keyboard,
  FreeMode,
]);

const HotelPageGallerySwiper = ({
  images,
  video,
  isMobile,
  title,
  hotelId,
}: HotelPageGallerySwiperProps) => {
  const galleryImages = images.map((image) => ({
    fullImage: image.fullImage,
    smallImage: image.thumbImageX2,
    id: image.id,
    thumbImage: image.smallImage,
  })) as ImageProps[];
  const defaultParams = {
    navigation,
    lazyPreloadPrevNext: 6,
    loop: images.length > 7,
  };
  const paramsSwiper: SwiperOptions = {
    slidesPerView: isMobile ? 1 : ('auto' as const),
    mousewheel: {
      forceToAxis: true,
    },
    freeMode: isMobile
      ? false
      : {
          enabled: true,
          sticky: false,
          momentumBounce: false,
        },
    slidesPerGroup: isMobile ? 1 : 3,
    speed: 600,
    pagination: isMobile
      ? {
          type: 'fraction',
        }
      : false,
    ...defaultParams,
  };
  const paramsSmallSwiper = {
    mousewheel: true,
    freeMode: {
      enabled: true,
      sticky: false,
      momentumBounce: false,
    },
    navigation,
  };
  let timer = 0;

  const [opened, setOpened] = useState(false);
  const [realIndexSlide, setRealIndexSlide] = useState(0);
  const [swiperGallery, setSwiperGallery] = useState<null | SwiperType>(null);
  const [thumbsSwiper, setThumbsSwiper] = useState<null | SwiperType>(null);
  const [swiperGalleryPopup, setSwiperGalleryPopup] =
    useState<null | SwiperType>(null);

  const showVideo = !!video;

  const openPopup = (index: number, id: number) => {
    const openVideoPopup = showVideo && index === 0;
    if (isMobile && !openVideoPopup) return;

    setRealIndexSlide(index);
    setOpened(true);
    timer = Date.now();

    if (!openVideoPopup) {
      pushOpenGalleryAnalytics(id);
    }
  };

  const closePopup = (id?: number) => {
    setOpened(false);
    timer = 0;

    if (!showVideo || realIndexSlide !== 0) {
      pushCloseGalleryAnalytics(id || 0);
    }
  };

  const imageOnErrorHandler = () => {
    pushStatsBrokenImage({
      hotel_id: Number(hotelId) || 0,
      image_type: 'hotel_gallery',
      error: '',
      room_name: '',
    });
  };

  const changeSlide = () => {
    if (swiperGalleryPopup && swiperGallery && !swiperGalleryPopup.destroyed) {
      const prevIdImage = images[swiperGalleryPopup.previousIndex].id;
      swiperGallery.slideTo(swiperGalleryPopup.activeIndex);
      if (!showVideo || swiperGalleryPopup.previousIndex !== 0) {
        pushShowImageAnalytics(prevIdImage, Date.now() - timer);
      } else {
        const iframeElement = document.querySelector('.yt_player_iframe');
        if (!(iframeElement instanceof HTMLIFrameElement)) return;

        iframeElement.contentWindow?.postMessage(
          '{"event":"command","func":"pauseVideo","args":""}',
          '*',
        );
      }
    } else return;

    timer = Date.now();
  };
  const swiperPopupProps: PopupSwiperProps = {
    opened,
    video,
    images: images.map((image) => image.fullImage),
    title,
    hotelId: Number(hotelId),
    isMobile,
    swiperGalleryPopup,
    realIndexSlide,
    closePopup,
    setSwiperGalleryPopup,
    changeSlide,
  };
  return (
    <>
      <GallerySwiper
        images={galleryImages}
        video={video}
        isMobile={isMobile}
        title={title}
        hotelId={Number(hotelId)}
        paramsSwiper={paramsSwiper}
        setSwiperGallery={setSwiperGallery}
        isLooped={swiperGallery?.loopedSlides !== undefined}
        openPopup={openPopup}
        imageOnError={imageOnErrorHandler}
        thumbsSwiper={thumbsSwiper}
        realIndexSlide={realIndexSlide}
      >
        <ArrowComponent isMobile={isMobile} />
      </GallerySwiper>
      {!isMobile ? (
        <ThumbSwiper
          paramsSmallSwiper={paramsSmallSwiper}
          setThumbsSwiper={setThumbsSwiper}
          images={galleryImages}
          title={title}
          video={video}
        />
      ) : null}
      <PopupSwiper {...swiperPopupProps} />
    </>
  );
};

export default HotelPageGallerySwiper;
